import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'cvlib';
import PinFormContainer from 'containers/PinForm';
import { hideEpgTransactions } from 'containers/Player/actions/epgTransactions/epgTransactions.actions';
import SingleOfferScreen from './SingleOfferScreen';
import MultipleOffersScreen from './MultipleOffersScreen';
import CallCenterScreen from './CallCenterScreen';
import './EpgTransactions.scss';
import { useDisableScrollInModal } from '../../../../hooks/useDisableScrollInModal';
import { clearWorkflowStart } from 'action-creators/payway/workflowStart';
import { CLAROPAGOS_GATE } from 'containers/Payment/CreditCard/constants';
import { clearCurrentPBI } from 'action-creators/payway/currentPayment/currentPBI';
import { PlayerContext } from '../../context/PlayerContext';
import { selectHeadbandState } from 'selectors/headband';
import { z_index } from 'const/zIndex';

const EpgTransactions = () => {
  const dispatch = useDispatch();
  const epgTransactions = useSelector(
    state => state.Player.EpgTransactions.epgTransactions,
  );
  const { liveVideoTagRef } = useContext(PlayerContext);
  const pbi = useSelector(state => state.payway.purchaseButtonInfo);
  const translations = useSelector(state => state.translations);
  const assets = useSelector(state => state.asset);
  const reduxZIndex = useSelector(state => state.Player.Modals.zIndex);
  const [modalIndex, setModalIndex] = useState(reduxZIndex);
  const [wfscleared, setWfscleared] = useState(false);
  const [forceCallCenter, setForceCallCenter] = useState(false);

  useEffect(
    () => {
      const clearWFS = async () => {
        // Limpia la WFS para no mostrar checkout
        await dispatch(clearWorkflowStart());
        setWfscleared(true);
      };
      clearWFS();
    },
    [modalIndex],
  );
  useDisableScrollInModal(epgTransactions?.isOpen);

  useDisableScrollInModal(epgTransactions.isOpen);

  useEffect(
    () => {
      if (!epgTransactions.isOpen || !liveVideoTagRef?.current) return;

      const handleOnLoad = () => {
        if (epgTransactions.isOpen && liveVideoTagRef?.current) {
          liveVideoTagRef?.current.pause();
        }
      };
      const events = ['loadstart', 'canplay', 'loadeddata', 'loadedmetadata'];

      events.forEach(event => {
        liveVideoTagRef?.current?.addEventListener(event, handleOnLoad);
      });

      if (epgTransactions.isOpen) {
        setModalIndex(reduxZIndex);
      }

      return () => {
        events.forEach(event => {
          liveVideoTagRef?.current?.removeEventListener(event, handleOnLoad); // removeEventListener en lugar de addEventListener
        });
      };
    },
    [epgTransactions.isOpen, liveVideoTagRef?.current],
  );

  // Efecto para manejar el botón back del navegador
  useEffect(
    () => {
      if (!epgTransactions.isOpen) return;

      const handleBrowserBack = event => {
        const fromCheckout = sessionStorage.getItem('fromCheckoutBack');
        if (fromCheckout === 'true') {
          return; // Ignora si viene del checkout
        }
        event.preventDefault();

        // Ejecutar la misma acción que el botón de salida
        handlePlayerControlExit();
      };

      window.addEventListener('popstate', handleBrowserBack);

    //  window.history.pushState({ modalOpen: true }, '');

      return () => {
        window.removeEventListener('popstate', handleBrowserBack);

        if (window.history.state?.modalOpen) {
        //  window.history.back();
        }
      };
    },
    [epgTransactions.isOpen],
  );

  const closeOnForceCallCenter = () => {
    if (forceCallCenter) return setForceCallCenter(false);
  };

  const handlePlayerControlExit = () => {
    if (forceCallCenter) return setForceCallCenter(false);
    dispatch(hideEpgTransactions());
    if (liveVideoTagRef?.current?.paused) liveVideoTagRef?.current.play();
  };

  const ScreenWithExit = useCallback(
    ({ children }) =>
      epgTransactions.isOpen && (
        <>
          <PinFormContainer />
          <h2
            className="player-epg-transactions-exit-button"
            style={{
              position: 'fixed',
              top: 0,
              zIndex: 1000,
            }}
          >
            <span
              className="player-controls-exit-button"
              onClick={handlePlayerControlExit}
            >
              {assets?.BotonGeneral_Icono_Regresar ? (
                <img
                  alt=""
                  src={assets?.BotonGeneral_Icono_Regresar}
                  className="player-controls-exit-button__logo"
                />
              ) : (
                <span className="player-controls-exit-button__logo" />
              )}

              {typeof translations.BotonGeneral_TextoTitulo_Salir === 'string'
                ? translations.BotonGeneral_TextoTitulo_Salir
                : ''}
            </span>
          </h2>
          {children}
        </>
      ),
    [epgTransactions.isOpen, assets, translations, forceCallCenter],
  );

  const modalStyle = React.useMemo(
    () => ({
      backgroundColor: '#131313',
      opacity: 1,
    }),
    [],
  );

  //SE FORZA CALLCENTER EN MULTIPLE OFERTA
  if (pbi?.data && forceCallCenter) {
    return (
      <ScreenWithExit>
        <Modal
          isOpen={epgTransactions.isOpen}
          backgroundStyle={modalStyle}
          zIndex={z_index.modalDefaultValue}
        >
          <CallCenterScreen
            forceCallCenter={forceCallCenter}
            setForceCallCenter={setForceCallCenter}
          />
        </Modal>
      </ScreenWithExit>
    );
  }

  // UNA SOLA OFERTA
  if (
    pbi?.data?.listButtons?.button?.length === 1 &&
    pbi?.data?.listButtons?.button?.[0]?.purchasable === true
  ) {
    return (
      <ScreenWithExit>
        <Modal
          isOpen={epgTransactions.isOpen}
          backgroundStyle={modalStyle}
          zIndex={z_index?.modalDefaultValue}
        >
          <SingleOfferScreen wfscleared={wfscleared} />
        </Modal>
      </ScreenWithExit>
    );
  }

  if (
    pbi?.data?.listButtons?.button?.length > 1 &&
    pbi?.data?.listButtons?.button?.some(offer => offer.purchasable === true)
  ) {
    return (
      <ScreenWithExit>
        <Modal
          isOpen={epgTransactions.isOpen}
          backgroundStyle={modalStyle}
          zIndex={z_index?.modalDefaultValue}
        >
          <MultipleOffersScreen
            wfscleared={wfscleared}
            setForceCallCenter={setForceCallCenter}
          />
        </Modal>
      </ScreenWithExit>
    );
  }

  if (
    pbi?.data &&
    (!pbi?.data?.listButtons?.button ||
      pbi?.data?.listButtons?.button?.length === 0 ||
      pbi?.data?.listButtons?.button?.every(
        offer => offer.purchasable === false,
      ))
  ) {
    return (
      <ScreenWithExit>
        <Modal
          isOpen={epgTransactions.isOpen}
          backgroundStyle={modalStyle}
          zIndex={z_index.modalDefaultValue}
        >
          <CallCenterScreen
            forceCallCenter={forceCallCenter}
            setForceCallCenter={setForceCallCenter}
          />
        </Modal>
      </ScreenWithExit>
    );
  }

  return null;
};

export default EpgTransactions;
